import { Controller } from "@hotwired/stimulus"
import { formatRatio, fullHex, minContrast } from "../lib/color_contrast"
import { backgroundsOf, colorOf as colorOfField, valueOf } from "../lib/color_fields"

const ACCENT_FIELDS = ["accent_color", "accent_text_color"]

// Live WCAG contrast warnings under colour pickers (ai/shared/_color_field). Each warning names its field, what
// that is drawn on (data-contrast-check-against fields and/or -against-color) and the minimum ratio, which it must
// reach on each. A blank field counts as its default. Dispatches contrast-check:checked after every check.
//
// Defaults that follow other colours are worked out before each check: data-color-default-same-as is another
// field's colour; data-color-default-inherited / -stock (the accent pair) is the global theme's accent while the
// channel inherits it, else the widget's stock one (as Ai::WebChatThemingHelper#web_chat_inherits_global_theme?).
export default class extends Controller {
  static targets = ["warning"]
  static values = { storedAppearance: Boolean }

  connect() {
    this.indexFields()
    // A save's morph hides every warning again without reconnecting: re-index (fields may be new) and re-check.
    this.refresh = () => {
      this.indexFields()
      this.check()
    }
    document.addEventListener("turbo:morph", this.refresh)
    this.check()
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.refresh)
  }

  // On every committed change in the section. Re-checks every warning: a dozen, each a few sums.
  check() {
    this.updateDefaults()
    this.warningTargets.forEach((warning) => this.checkWarning(warning))
    this.dispatch("checked")
  }

  checkWarning(warning) {
    const { contrastCheckField: field, contrastCheckMin: min } = warning.dataset
    const ratio = minContrast(this.colorOf(field), backgroundsOf(warning, (attribute) => this.colorOf(attribute)))
    const failing = ratio !== null && ratio < Number(min)
    // The class, not the hidden attribute: the warning's display:flex (an inline note) would outrank [hidden].
    warning.classList.toggle("hidden", !failing)
    if (failing) warning.querySelector("[data-contrast-check-ratio]").textContent = formatRatio(ratio)
    this.describe(field, warning, failing)
  }

  // A showing warning describes the field's hex input, for screen readers. Only while it shows: a hidden
  // element is still read as a description.
  describe(attribute, warning, failing) {
    const text = this.fields.get(attribute)?.querySelector("[data-color-field-target='text']")
    if (!text || !warning.id) return

    if (failing) text.setAttribute("aria-describedby", warning.id)
    else text.removeAttribute("aria-describedby")
  }

  updateDefaults() {
    const inheriting = this.inheritsGlobalTheme()
    this.fields.forEach((root) => {
      const { colorDefaultSameAs: sameAs, colorDefaultInherited: inherited, colorDefaultStock: stock } = root.dataset
      if (sameAs) this.setDefault(root, this.colorOf(sameAs))
      else if (inherited && stock) this.setDefault(root, inheriting ? inherited : stock)
    })
  }

  // The field's default, also shown while it's blank: on the swatch, and in a hex placeholder (unless that says
  // something else, like "None").
  setDefault(root, color) {
    const hex = fullHex(color)
    if (!hex) return

    root.dataset.colorFieldDefaultValue = hex.toUpperCase()
    if (valueOf(root)) return
    const picker = root.querySelector("[data-color-field-target='picker']")
    const text = root.querySelector("[data-color-field-target='text']")
    if (picker) picker.value = hex
    if (text?.placeholder.startsWith("#")) text.placeholder = hex.toUpperCase()
  }

  // See the class comment. Setting the first appearance colour still counts: that save copies the global accents
  // in (Integration::TwilioWeb#inherit_theme_accent_colors), unless some were saved before (storedAppearance).
  inheritsGlobalTheme() {
    if (ACCENT_FIELDS.some((attribute) => valueOf(this.fields.get(attribute)))) return false

    const appearanceSet = [...this.fields].some(([attribute, root]) => !ACCENT_FIELDS.includes(attribute) && valueOf(root))
    return !appearanceSet || !this.storedAppearanceValue
  }

  // A field's committed colour, or its default when blank.
  colorOf(attribute) {
    return colorOfField(this.fields.get(attribute))
  }

  // Every colour field's root by its attribute (the first wins, as with querySelector).
  indexFields() {
    this.fields = new Map()
    this.element.querySelectorAll("[data-color-attribute]").forEach((root) => {
      if (!this.fields.has(root.dataset.colorAttribute)) this.fields.set(root.dataset.colorAttribute, root)
    })
  }
}
